import * as React from "react";
import {
  Header,
  Container,
  ColumnLayout,
  SpaceBetween,
  StatusIndicator,
  Popover,
  Box,
  ProgressBar,
  Alert,
} from "@amzn/awsui-components-react";
import { getSavingsArray, USDollar } from "../util";
import ValueWithLabel from "shared/programs/migration-acceleration-program/2024/fund-request/components/fields/ValueWithLabel";
import RecommendationStatuses from "./recommendation-statuses/RecommendationStatuses";
import Savings from "./savings/Savings";
import { CalcBusterItem, ServiceItem } from "../calcBusterTypes";
import { groupBy } from "shared/util/common/util";
import ModernizationsDetails from "./modernizationDetails/ModernizationDetails";
import FundingInsights from "./fundingInsights/FundingInsights";

const SummarySection: React.FC<{ item: CalcBusterItem }> = ({ item }) => {
  const arr = USDollar.format(item["annualRunRate"]);
  const support = item["support"] ?? {};
  const modernizations = item["modernizations"] ?? {};
  const funding = item["funding"];
  const recommendations = item["recommendations"] ?? [];
  const services = item["services"] ?? [];

  const errors = item["errors"] ?? [];
  const errorsBySev = groupBy(errors, "severity");
  const numServicesError = errorsBySev["ERROR"]
    ? errorsBySev["ERROR"].length
    : 0;
  const numServicesSuccess = services.length - numServicesError;

  const containsEks =
    services.find((s: ServiceItem) => s.serviceCode === "awsEks") ?? false;
  const containsEC2 =
    services.find((s: ServiceItem) => s.serviceCode === "ec2Enhancement") ??
    false;

  const numCols = funding ? 3 : 2;

  const ModernizationIndexLabel = () => {
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Box>Modernization Index</Box>
        <Popover
          dismissButton={false}
          position="top"
          size="medium"
          triggerType="custom"
          content={
            <Box variant="p">
              This is the percentage of the annual run rate from modern services.
            </Box>
          }
        >
          <StatusIndicator type="info" />
        </Popover>
      </SpaceBetween>
    )
  }

  const GeneralColumn = () => {
    return (
      <SpaceBetween size="l">
        <Box variant="h3" padding="n">
          General
        </Box>
        <ValueWithLabel label="Annual Run Rate">{arr}</ValueWithLabel>
        <ValueWithLabel label="Number of services estimate processed">
          <SpaceBetween size={"l"} direction={"horizontal"}>
            <StatusIndicator> {numServicesSuccess}</StatusIndicator>
            <StatusIndicator type="error"> {numServicesError}</StatusIndicator>
          </SpaceBetween>
        </ValueWithLabel>
        <ProgressBar
          variant="key-value"
          label={ModernizationIndexLabel()}
          value={modernizations.index}
          additionalInfo={
            containsEks && containsEC2 ? (
              <Alert type="warning">
                <Box>
                  <b>
                    The AWS Pricing Calculator includes Elastic Kubernetes
                    Service (EKS) and Elastic Cloud Compute Service (EC2)
                    estimations.
                  </b>
                </Box>
                <Box>
                  CalcBuster cannot automatically associate the EC2 instances
                  with the EKS clusters. This may result in an incorrect
                  modernization index, as EC2 cost is not included in the modern
                  services ARR. Please involve a PSA to do a deep dive.
                </Box>
              </Alert>
            ) : (
              <></>
            )
          }
          description={
            <Popover
              dismissButton={false}
              position="top"
              size="large"
              content={
                <Container>{ModernizationsDetails(modernizations)}</Container>
              }
            >
              index %
            </Popover>
          }
        />
      </SpaceBetween>
    );
  };

  const OptimizationsColumn = () => {
    return (
      <SpaceBetween size="l">
        <Box variant="h3" padding="n">
          Optimizations
        </Box>
        <ValueWithLabel label="Recommendations">
          <SpaceBetween direction="horizontal" size="xs">
            {RecommendationStatuses(recommendations)}
          </SpaceBetween>
        </ValueWithLabel>
        <ValueWithLabel label="Recommendations may lower ARR by">
          <Popover
            dismissButton={false}
            position="top"
            size="medium"
            content={<Container>{Savings(recommendations)}</Container>}
          >
            <SpaceBetween direction="horizontal" size="xs">
              <Box>
                {USDollar.format(
                  getSavingsArray(recommendations)
                    .map((v) => v.value)
                    .reduce((partialSum, a) => partialSum + a, 0)
                )}
              </Box>
            </SpaceBetween>
          </Popover>
        </ValueWithLabel>
      </SpaceBetween>
    );
  };

  return (
    <Container header={<Header variant="h3">Summary</Header>}>
      <ColumnLayout columns={numCols} variant="text-grid">
        {GeneralColumn()}
        {OptimizationsColumn()}
        {funding ? FundingInsights(funding, support) : null}
      </ColumnLayout>
    </Container>
  );
};

export default SummarySection;
